/**
 * Extracts a YouTube video ID from a URL.
 * Supports youtube.com/watch, youtu.be, youtube.com/embed, youtube.com/shorts,
 * youtube.com/live, and youtube-nocookie.com variants.
 * Returns null if the URL is not a recognized YouTube URL.
 */
export function extractYouTubeVideoId(url: string): string | null {
    try {
        const urlObj = new URL(url)
        const hostname = urlObj.hostname.replace(/^(www|m)\./, '')

        // youtu.be/VIDEO_ID
        if (hostname === 'youtu.be') {
            const id = urlObj.pathname.slice(1).split('/')[0]
            return id && /^[\w-]{11}$/.test(id) ? id : null
        }

        // youtube.com or youtube-nocookie.com
        if (hostname === 'youtube.com' || hostname === 'youtube-nocookie.com') {
            // /watch?v=VIDEO_ID
            if (urlObj.pathname === '/watch') {
                const id = urlObj.searchParams.get('v')
                return id && /^[\w-]{11}$/.test(id) ? id : null
            }

            // /embed/VIDEO_ID, /shorts/VIDEO_ID, /live/VIDEO_ID
            const match = urlObj.pathname.match(/^\/(embed|shorts|live)\/([\w-]{11})/)
            if (match) {
                return match[2]
            }
        }

        return null
    } catch {
        return null
    }
}

/**
 * Spotify content types that can be embedded.
 */
export type SpotifyContentType = 'track' | 'album' | 'playlist' | 'episode' | 'show' | 'artist'

/**
 * Extracted Spotify embed info.
 */
export interface SpotifyInfo {
    type: SpotifyContentType
    id: string
}

const SPOTIFY_CONTENT_TYPES = new Set<string>([
    'track',
    'album',
    'playlist',
    'episode',
    'show',
    'artist',
])

/**
 * Extracts Spotify content type and ID from a URL.
 * Supports open.spotify.com/{type}/{id} with optional /intl-XX/ prefix and query params.
 * Returns null if the URL is not a recognized Spotify URL.
 */
export function extractSpotifyInfo(url: string): SpotifyInfo | null {
    try {
        const urlObj = new URL(url)
        const hostname = urlObj.hostname.replace(/^(www|m)\./, '')

        if (hostname !== 'open.spotify.com') {
            return null
        }

        // Path: optional /intl-XX/ prefix, then /{type}/{id}
        const match = urlObj.pathname.match(
            /^(?:\/intl-[a-z]{2,3})?\/([a-z]+)\/([a-zA-Z0-9]{1,32})/
        )
        if (!match) {
            return null
        }

        const [, type, id] = match
        if (!SPOTIFY_CONTENT_TYPES.has(type)) {
            return null
        }

        return { type: type as SpotifyContentType, id }
    } catch {
        return null
    }
}

/**
 * Extracts a tweet/post ID from a Twitter or X URL.
 * Supports twitter.com/{user}/status/{id} and x.com/{user}/status/{id}
 * with optional mobile. prefix and query params.
 * Returns null if the URL is not a recognized Twitter/X URL.
 */
export function extractTweetId(url: string): string | null {
    try {
        const urlObj = new URL(url)
        let hostname = urlObj.hostname
        if (hostname.startsWith('www.')) {
            hostname = hostname.slice(4)
        }
        if (hostname.startsWith('mobile.')) {
            hostname = hostname.slice(7)
        }

        if (hostname !== 'twitter.com' && hostname !== 'x.com') {
            return null
        }

        // Path: /{username}/status/{tweetId}
        const match = urlObj.pathname.match(/^\/[^/]+\/status\/(\d{1,25})/)
        if (!match) {
            return null
        }

        return match[1]
    } catch {
        return null
    }
}

/**
 * Extracts a Nostr event ID from a Divine.video URL.
 * Supports divine.video/video/<64-char-hex> (case-insensitive host).
 * Returns null if the URL is not a recognized Divine.video video URL.
 */
export function extractDivineVideoEventId(url: string): string | null {
    try {
        const urlObj = new URL(url)
        const hostname = urlObj.hostname.replace(/^www\./, '').toLowerCase()

        if (hostname !== 'divine.video') {
            return null
        }

        const match = urlObj.pathname.match(/^\/video\/([0-9a-f]{64})$/i)
        if (!match) {
            return null
        }

        return match[1].toLowerCase()
    } catch {
        return null
    }
}

/**
 * Detects if a URL points to a GIF file.
 * Checks for .gif extension (case-insensitive) or format=gif query param.
 */
export function isGifUrl(url: string): boolean {
    try {
        const urlObj = new URL(url)
        const pathname = urlObj.pathname.toLowerCase()

        // Check file extension (case-insensitive)
        if (pathname.endsWith('.gif')) {
            return true
        }

        // Check format query parameter (common with CDNs)
        const format = urlObj.searchParams.get('format')?.toLowerCase()
        if (format === 'gif') {
            return true
        }

        return false
    } catch {
        // Invalid URL - fall back to simple check
        return url.toLowerCase().includes('.gif')
    }
}

/**
 * Heuristic: could this external link be a fediverse/ActivityPub post?
 *
 * This only GATES whether we attempt to resolve the link as an AP post — it does
 * not assert the link IS fediverse. Verification happens by actually fetching the
 * post (see FediverseEmbed). The heuristic exists so we don't fire a resolve fetch
 * for every external link in a timeline.
 *
 * Positive signals:
 *  - The URL path matches a known fediverse post shape (Mastodon, Pleroma/Akkoma,
 *    Misskey/Firefish, generic ActivityStreams).
 *  - The external card title carries a fediverse byline like "(@user@domain)".
 *
 * @param url   The external embed URL.
 * @param title Optional external card title (e.g. "David Smith (@Catfish_Man@mastodon.social)").
 */
export function isLikelyFediversePostUrl(url: string, title?: string): boolean {
    // Require a valid http(s) URL before any positive signal — prevents javascript:/data: URIs.
    let parsed: URL
    try {
        parsed = new URL(url)
    } catch {
        return false
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return false
    }

    // Byline signal: "(@user@domain)" — covers instances whose post URLs we don't
    // pattern-match. Domain part must contain a dot to look like a hostname.
    if (title && /\(@[^@\s)]+@[^@\s)]+\.[^@\s)]+\)/.test(title)) {
        return true
    }

    const { pathname } = parsed

    const fediversePostPatterns: RegExp[] = [
        // Mastodon pretty:    /@user/123456789
        /^\/@[^/]+\/\d+\/?$/,
        // Mastodon pretty w/ statuses: /@user/statuses/id
        /^\/@[^/]+\/statuses\/[^/]+\/?$/,
        // Mastodon canonical: /users/user/statuses/123456789
        /^\/users\/[^/]+\/statuses\/[^/]+\/?$/,
        // Misskey/Firefish:   /notes/<id>
        /^\/notes\/[^/]+\/?$/,
        // Pleroma/Akkoma:     /objects/<uuid>  or  /notice/<id>
        /^\/objects\/[0-9a-f-]+\/?$/i,
        /^\/notice\/[^/]+\/?$/,
    ]

    return fediversePostPatterns.some(re => re.test(pathname))
}

// Duplicated from mobile/react-native/lib/embed.ts (isSameFediversePost et al.)
// rather than shared via @mackinac/shared — see the fix/webapp-bridged-card-identity
// plan's D3 for why. Keep the two in sync by hand.

interface FediversePostIdentity {
    host: string
    family: 'mastodon' | 'misskey' | 'pleroma-object' | 'pleroma-notice'
    id: string
}

/**
 * Parses a fediverse post URL into a route-family-aware identity, so e.g.
 * Mastodon's pretty (`/@user/123`), pretty-with-statuses (`/@user/statuses/123`),
 * and canonical (`/users/user/statuses/123`) forms all collapse to the same
 * `{host, family, id}` — matching the route patterns in `isLikelyFediversePostUrl`.
 * Returns undefined for unrecognized shapes; callers must not fall back to a
 * generic last-path-segment compare, which would collapse unrelated routes.
 */
function parseFediversePostIdentity(url: string): FediversePostIdentity | undefined {
    let parsed: URL
    try {
        parsed = new URL(url)
    } catch {
        return undefined
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return undefined
    }

    const host = parsed.host.toLowerCase().replace(/^www\./, '')
    const { pathname } = parsed

    let match = pathname.match(/^\/@[^/]+\/(\d+)\/?$/)
    if (match?.[1]) return { host, family: 'mastodon', id: match[1] }

    match = pathname.match(/^\/@[^/]+\/statuses\/([^/]+)\/?$/)
    if (match?.[1]) return { host, family: 'mastodon', id: match[1] }

    match = pathname.match(/^\/users\/[^/]+\/statuses\/([^/]+)\/?$/)
    if (match?.[1]) return { host, family: 'mastodon', id: match[1] }

    match = pathname.match(/^\/notes\/([a-z0-9]{8,})\/?$/i)
    if (match?.[1]) return { host, family: 'misskey', id: match[1].toLowerCase() }

    match = pathname.match(/^\/objects\/([0-9a-f-]+)\/?$/i)
    if (match?.[1]) return { host, family: 'pleroma-object', id: match[1].toLowerCase() }

    match = pathname.match(/^\/notice\/([a-z0-9]{8,})\/?$/i)
    if (match?.[1]) return { host, family: 'pleroma-notice', id: match[1].toLowerCase() }

    return undefined
}

/** Normalizes a URL to `host+pathname` (lowercased host, `www.` and one trailing slash stripped, query/fragment dropped) for exact-equality comparison. Returns undefined if unparseable or non-http(s). */
function normalizeUrlForEquality(url: string): string | undefined {
    let parsed: URL
    try {
        parsed = new URL(url)
    } catch {
        return undefined
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return undefined
    }
    const host = parsed.host.toLowerCase().replace(/^www\./, '')
    const pathname =
        parsed.pathname.endsWith('/') && parsed.pathname !== '/'
            ? parsed.pathname.slice(0, -1)
            : parsed.pathname
    return `${host}${pathname}`
}

/**
 * Do two URLs identify the same fediverse post? Used to tell whether a
 * Bridgy Fed external card actually refers to the post/marker it's being
 * compared against, rather than merely "looks fediverse-shaped".
 *
 * Two checks, in order:
 *  1. Exact equality after normalizing (host+pathname, `www.` and trailing
 *     slash stripped) — catches byte-identical URLs on instance software
 *     whose route shape isn't in the family list below.
 *  2. Route-family identity (`{host, family, id}`) — catches the same post
 *     expressed in different URL forms (e.g. `/@user/123` vs
 *     `/users/user/statuses/123`), which is common: the marker text and the
 *     external card frequently use different forms for the same status.
 *
 * Deliberately fails closed: an unrecognized URL shape on either side never
 * falls back to a generic path-segment compare, since that would collapse
 * unrelated routes into false matches.
 */
export function isSameFediversePost(a: string | undefined, b: string | undefined): boolean {
    if (!a || !b) return false

    const normA = normalizeUrlForEquality(a)
    const normB = normalizeUrlForEquality(b)
    if (normA !== undefined && normA === normB) return true

    const idA = parseFediversePostIdentity(a)
    const idB = parseFediversePostIdentity(b)
    if (!idA || !idB) return false

    return idA.host === idB.host && idA.family === idB.family && idA.id === idB.id
}
